/**
 * ChatLongMessageNotice
 *
 * Shows a notice under the chat input when the chat is getting long,
 * suggesting to start a new chat with a summary.
 *
 * Primary responsibilities:
 * - Calculate total token count for the current chat
 * - Show a warning and action when approaching context window limits
 * - Create new chat with summary of previous conversation
 *
 * Dependencies:
 * - lib/tokens/count-tokens (lazy gpt-tokenizer)
 * - ChatbotUIChatContext
 */

import { useContext, useMemo, useState } from 'react'
import { ChatbotUIChatContext } from '@/context/chat'
import { ChatbotUIContext } from '@/context/context'
import { countTokens } from '@/lib/tokens/count-tokens'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { useChatHandler } from '@/components/chat/chat-hooks/use-chat-handler'
import { logger } from '@/lib/logger'
import { isFreePlan } from '@/lib/subscription'
import { useDelayedPaywall } from '@/hooks/use-delayed-paywall'

const TOKEN_THRESHOLD = 18000 // Show warning at 18k tokens (for 20k+ context models)
const FREE_PLAN_TOKEN_LIMIT = 50000 // Hard guidance limit for the Free plan notice

export const ChatLongMessageNotice = () => {
  const { t } = useTranslation()
  const [isCreatingNewChat, setIsCreatingNewChat] = useState(false)

  const { chatMessages, selectedChat } = useContext(ChatbotUIChatContext)

  const { profile, setIsPaywallOpen } = useContext(ChatbotUIContext)
  const scheduleUpgradePaywall = useDelayedPaywall(
    setIsPaywallOpen ? () => setIsPaywallOpen(true) : undefined
  )

  const { handleNewChat } = useChatHandler()

  const totalTokens = useMemo(
    () =>
      chatMessages.reduce((sum, msg) => {
        if (typeof msg.message.content === 'string') {
          return sum + countTokens(msg.message.content)
        }
        // If multimodal, count text tokens only for simplicity
        if (Array.isArray(msg.message.content)) {
          return (
            sum +
            (msg.message.content as Array<{ type: string; text?: string }>).reduce(
              (s: number, item) => {
                if (item.type === 'text') return s + countTokens(item.text || '')
                return s
              },
              0
            )
          )
        }
        return sum
      }, 0),
    [chatMessages]
  )

  const userIsFree = isFreePlan(profile)

  // If under general notice threshold, render nothing
  if (!userIsFree && totalTokens < TOKEN_THRESHOLD) return null
  if (userIsFree && totalTokens < Math.min(FREE_PLAN_TOKEN_LIMIT, TOKEN_THRESHOLD)) return null

  const handleStartNewChatWithSummary = async () => {
    if (isCreatingNewChat) return

    setIsCreatingNewChat(true)

    try {
      // 1. Create a text block of the conversation to be summarized
      const conversationToSummarize = JSON.stringify(chatMessages)

      // 2. Call the AI model to generate a proper summary
      logger.debug('Making API call to generate summary...')
      const summaryResponse = await fetch('/api/create-chat-summary', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ conversationToSummarize })
      })

      if (!summaryResponse.ok) {
        const errorText = summaryResponse.text()
        logger.error('Summary API error:', errorText)
        throw new Error(`Failed to generate summary: ${summaryResponse.status}`)
      }

      const generatedSummary = (await summaryResponse.text()) || 'Summary could not be generated.'
      logger.debug('Generated response:', summaryResponse)
      logger.debug('Generated summary:', generatedSummary)
      // Check for and apply previous chat context RIGHT HERE

      // 3. Store the AI-generated summary in the context for the new chat
      const previousChatContext = {
        chatId: selectedChat?.id || 'unknown',
        chatName: selectedChat?.name || 'Previous Conversation',
        messageCount: chatMessages.length,
        summary: generatedSummary, // Use the AI-generated summary
        timestamp: new Date().toISOString()
      }

      // Store in localStorage to pass to new chat
      localStorage.setItem('previousChatContext', JSON.stringify(previousChatContext))

      // Use the proper handleNewChat function to avoid page reload
      await handleNewChat()
    } catch (error) {
      console.error('Error creating new chat with summary:', error)
    } finally {
      setIsCreatingNewChat(false)
    }
  }

  // Free plan at/over 50k tokens: show the upgrade notice plus summary option
  if (userIsFree && totalTokens >= FREE_PLAN_TOKEN_LIMIT) {
    return (
      <div className="mb-1 mt-2 text-center text-xs text-violet-700 dark:text-violet-300">
        <button
          type="button"
          onClick={() => scheduleUpgradePaywall()}
          className="font-medium underline decoration-dotted underline-offset-2 hover:text-violet-900 dark:hover:text-violet-100"
        >
          <span className="font-semibold">
            {t(
              'chat_input.plus_upgrade_value',
              'Get Plus for longer chats and bigger uploads.'
            )}
          </span>
        </button>{' '}
        <button
          className="ml-2 inline-flex items-center gap-1 font-semibold underline transition hover:text-violet-900 disabled:opacity-50 dark:hover:text-violet-100"
          type="button"
          onClick={handleStartNewChatWithSummary}
          disabled={isCreatingNewChat}
        >
          {isCreatingNewChat && <Loader2 className="size-3 animate-spin" />}
          {t('chat_input.start_new_with_summary', 'Start new chat with summary')}
        </button>
      </div>
    )
  }

  // General long-chat warning (non-free or below 50k for free)
  return (
    <div className="mb-1 mt-2 text-center text-xs text-yellow-700 dark:text-yellow-300">
      {t('chat_input.long_chat_warning', 'Chat is getting long, ')}
      <button
        className="inline-flex items-center gap-1 font-semibold underline transition hover:text-yellow-900 disabled:opacity-50 dark:hover:text-yellow-100"
        type="button"
        onClick={handleStartNewChatWithSummary}
        disabled={isCreatingNewChat}
      >
        {isCreatingNewChat && <Loader2 className="size-3 animate-spin" />}
        {t('chat_input.start_new_with_summary', 'start new chat with summary.')}
      </button>
    </div>
  )
}
